import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import SimpleGrid from '~/common/components/Grids/Grid/SimpleGrid';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import { FORMULA1065 as FORMULA } from '~/pages/IncomeCalculator/constants';
import { FormTypes } from '~/pages/IncomeCalculator/enums';
import type { CalculationResult } from '~/pages/IncomeCalculator/types';

import BreakdownContainer from '../BreakdownContainer';
import lang from '../language.json';
import BreakdownValueWithChild from '../styles/BreakdownValueWithChild';
import BreakdownYearContainer from '../styles/BreakdownYearContainer';
import TrendingAnalysis from './TrendingAnalysis';

type BreakdownView1065Props = {
  currentYear: number;
  selectedResult: CalculationResult;
  showFullBreakdown: boolean;
};

type OneYearBreakdownProps = {
  years: number[];
  currentYear: number;
  selectedResult: CalculationResult;
};

type TwoYearBreakdownProps = {
  years: number[];
  currentYear: number;
  isPositiveTrend: string | undefined;
  formula: CalculationResult['formulas'][number] | undefined;
  selectedResult: CalculationResult;
};

type Form1065CalculationBreakdownProps = {
  selectedResult: CalculationResult;
  onNewWindow?: () => void;
  hideAccordion?: boolean;
};

const k1CombineBox = (
  selectedResult: CalculationResult,
  currentYear: number,
): number => {
  return (
    selectedResult.getInputAsFloat(
      FormTypes.ScheduleK1Form1065,
      currentYear,
      'ordinaryBusinessIncome',
    ) +
    selectedResult.getInputAsFloat(
      FormTypes.ScheduleK1Form1065,
      currentYear,
      'netRentalIncomeLoss',
    ) +
    selectedResult.getInputAsFloat(
      FormTypes.ScheduleK1Form1065,
      currentYear,
      'otherNetRentalIncomeLoss',
    )
  );
};

const totalAddbacksAndDeductions = (
  selectedResult: CalculationResult,
  currentYear: number,
): number => {
  return (
    selectedResult.getInputAsFloat(
      FormTypes.Form1065,
      currentYear,
      'depletion',
    ) +
    selectedResult.getInputAsFloat(
      FormTypes.Form1065,
      currentYear,
      'depreciation',
    ) +
    selectedResult.getInputAsFloat(
      FormTypes.Form1065,
      currentYear,
      'amortizationCasualtyLoss',
    ) -
    (selectedResult.getInputAsFloat(
      FormTypes.Form1065,
      currentYear,
      'ordinaryIncome',
    ) +
      (selectedResult.getInputAsFloat(
        FormTypes.Form1065,
        currentYear,
        'otherIncome',
      ) +
        selectedResult.getInputAsFloat(
          FormTypes.Form1065,
          currentYear,
          'mortgagesNotesBondsPayableInLessThanOneYear',
        ) +
        selectedResult.getInputAsFloat(
          FormTypes.Form1065,
          currentYear,
          'travelAndEntertainment',
        )))
  );
};

const annualIncome = (
  selectedResult: CalculationResult,
  currentYear: number,
): number => {
  return selectedResult.getInputAsFloat(
    FormTypes.Form1065,
    currentYear,
    'annualIncome',
  );
};

const sign = (value: number, defaultSign = '+'): string =>
  value < 0 ? '-' : defaultSign;

const BreakdownView1065 = ({
  currentYear,
  selectedResult,
  showFullBreakdown,
}: BreakdownView1065Props) => {
  const getVal = (formType: number, fieldName: string): number =>
    selectedResult.getInputAsFloat(formType, currentYear, fieldName);

  const {
    FIELDS: { ['1065']: FIELDS },
  } = lang;

  const medicareWages = getVal(FormTypes.W2, 'medicareWages');
  const distributionsA = getVal(FormTypes.ScheduleK1Form1065, 'distributions');
  const distributionsB = getVal(FormTypes.ScheduleK1Form1065, 'distributions2');
  const distributions = distributionsA + distributionsB;

  const useK1 =
    selectedResult
      .getInput(FormTypes.ScheduleK1Form1065, currentYear, 'utilizeK1Value')
      ?.fieldValue?.toLowerCase() === 'true';

  const k1Combined = k1CombineBox(selectedResult, currentYear);

  const hideGuranteedPayments = selectedResult.formulas.some((f) =>
    [
      FORMULA.OneYearMonthlyIncomeWithoutGuranteedPaymentsFHA,
      FORMULA.TwoYearMonthlyIncomeWithoutGuranteedPaymentsFHA,
      FORMULA.TwoYearMonthlyIncomeWithoutGuranteedPaymentsDU,
    ].includes(f.formulaId),
  );

  const k1Box1 = getVal(FormTypes.ScheduleK1Form1065, 'ordinaryBusinessIncome');
  const k1Box2 = getVal(FormTypes.ScheduleK1Form1065, 'netRentalIncomeLoss');
  const k1Box3 = getVal(
    FormTypes.ScheduleK1Form1065,
    'otherNetRentalIncomeLoss',
  );
  const guaranteedPaymentsRaw = getVal(
    FormTypes.ScheduleK1Form1065,
    'guaranteedPayments',
  );
  const guaranteedPayments = hideGuranteedPayments ? 0 : guaranteedPaymentsRaw;
  const percentageOwnership = getVal(
    FormTypes.ScheduleK1Form1065,
    'percentageOwnership',
  );

  const ordinaryIncome = getVal(FormTypes.Form1065, 'ordinaryIncome');
  const otherIncome = getVal(FormTypes.Form1065, 'otherIncome');
  const depreciation = getVal(FormTypes.Form1065, 'depreciation');
  const depletion = getVal(FormTypes.Form1065, 'depletion');
  const mortgagesNotesBonds = getVal(
    FormTypes.Form1065,
    'mortgagesNotesBondsPayableInLessThanOneYear',
  );
  const travelAndEntertainment = getVal(
    FormTypes.Form1065,
    'travelAndEntertainment',
  );
  const amortization = getVal(FormTypes.Form1065, 'amortizationCasualtyLoss');

  const addbacksTotal = totalAddbacksAndDeductions(selectedResult, currentYear);
  const k1OrDistributions = useK1 ? k1Combined : distributions;
  const annualIncomeValue = annualIncome(selectedResult, currentYear);
  const subTotal = getVal(FormTypes.Form1065, 'subTotal');
  const borrowerShare = getVal(
    FormTypes.Form1065,
    'borrowerShareOfIncomeOrLoss',
  );

  const renderFullBreakdown = () => (
    <>
      {useK1 ? (
        <BreakdownValueWithChild
          name={FIELDS.K1BOXES}
          value={k1Combined}
          isCurrency={true}
          childrenData={[
            { name: FIELDS.KBOX1, value: k1Box1, sign: sign(k1Box1) },
            { name: FIELDS.KBOX2, value: k1Box2, sign: sign(k1Box2) },
            { name: FIELDS.KBOX3, value: k1Box3, sign: sign(k1Box3) },
          ]}
          fontWeight="bold"
        />
      ) : (
        <BreakdownValueWithChild
          name={FIELDS.DISTRIBUTIONS}
          value={distributions}
          isCurrency={true}
          childrenData={[
            {
              name: FIELDS.DISTRIBUTIONS19A,
              value: distributionsA,
              sign: sign(distributionsA),
            },
            {
              name: FIELDS.DISTRIBUTIONS19B,
              value: distributionsB,
              sign: sign(distributionsB),
            },
          ]}
          fontWeight="bold"
        />
      )}
      <BreakdownValueWithChild
        name={FIELDS.GUARANTEED_PAYMENTS}
        value={guaranteedPayments}
        isCurrency={true}
        childrenData={[
          {
            name: FIELDS.K1BOX4C,
            value: guaranteedPayments,
            sign: sign(guaranteedPaymentsRaw),
          },
        ]}
        fontWeight="bold"
      />
      <BreakdownValueWithChild
        name={FIELDS.ADDBACK_DEDUCTIONS}
        value={addbacksTotal}
        isCurrency={true}
        childrenData={[
          {
            name: FIELDS.LINE4,
            value: ordinaryIncome,
            sign: sign(-ordinaryIncome),
          },
          { name: FIELDS.LINE7, value: otherIncome, sign: sign(-otherIncome) },
          {
            name: FIELDS.LINE16C,
            value: depreciation,
            sign: sign(depreciation),
          },
          { name: FIELDS.LINE17, value: depletion, sign: sign(depletion) },
          {
            name: FIELDS.LLINE16COLUMND,
            value: mortgagesNotesBonds,
            sign: sign(-mortgagesNotesBonds),
          },
          {
            name: FIELDS.M1LINE4B,
            value: travelAndEntertainment,
            sign: sign(-travelAndEntertainment),
          },
          {
            name: FIELDS.AMORTIZATION,
            value: amortization,
            sign: sign(amortization),
          },
        ]}
        fontWeight="bold"
      />
      <BreakdownValueWithChild
        name={FIELDS.PERCENTOWNERSHIP}
        value={percentageOwnership * 100}
        isCurrency={false}
        childrenData={[
          { name: FIELDS.K1BOXJ, value: percentageOwnership * 100 },
        ]}
        fontWeight="bold"
      />
      <BreakdownValueWithChild
        name={lang.MONTHLY_CALC}
        isCurrency={true}
        childrenData={[
          { name: '', value: medicareWages, sign: sign(medicareWages) },
          { name: '', value: k1OrDistributions, sign: sign(k1OrDistributions) },
          {
            name: '',
            value: guaranteedPayments,
            sign: sign(guaranteedPaymentsRaw),
          },
          {
            name: '',
            value: addbacksTotal,
            sign: sign(addbacksTotal),
            monthlyIncome: {
              show: true,
              percentValue: percentageOwnership,
              totalSumValue: annualIncomeValue,
            },
          },
        ]}
        fontWeight="bold"
      />
      <BreakdownValueWithChild
        name={currentYear + ' Calculated Monthly Income'}
        isCurrency={true}
        childrenData={[
          {
            name: '',
            value: annualIncomeValue / 12,
            sign: sign(annualIncomeValue, ''),
          },
        ]}
        fontWeight="bold"
      />
      <BreakdownValueWithChild
        name={FIELDS.PARTNERSHIP_SUBTOTAL}
        isCurrency={true}
        childrenData={[{ name: '', value: subTotal, sign: sign(subTotal, '') }]}
        fontWeight="bold"
      />
      <BreakdownValueWithChild
        name={FIELDS.BORROWER_SHARE_OF_PARTNER_SHIPINCOME}
        isCurrency={true}
        childrenData={[
          { name: '', value: borrowerShare, sign: sign(borrowerShare, '') },
        ]}
        fontWeight="bold"
      />
    </>
  );

  return (
    <BreakdownYearContainer title={currentYear}>
      <BreakdownValueWithChild
        name={FIELDS.WAGES}
        value={medicareWages}
        isCurrency={true}
        childrenData={[
          {
            name: FIELDS.W2BOX5,
            value: medicareWages,
            sign: sign(medicareWages),
          },
        ]}
        fontWeight="bold"
      />
      {showFullBreakdown ? (
        renderFullBreakdown()
      ) : (
        <Box>
          <Flex fontWeight="bold">
            <Text fontSize="sm">{lang.MONTHLY_CALC}</Text>
          </Flex>
          <Text
            fontSize="sm"
            marginLeft="10px"
            marginTop="2px"
            style={{ textIndent: '-6px' }}
            pl={4}
          >
            {formatCurrency(medicareWages)}
            &nbsp; &divide; 12 (Months)
          </Text>
        </Box>
      )}
    </BreakdownYearContainer>
  );
};

const OneYearBreakdown = ({
  years,
  currentYear,
  selectedResult,
}: Readonly<OneYearBreakdownProps>) => {
  return (
    <SimpleGrid
      w={`${50 * Math.min(years.length, 2)}%`}
      columns={Math.min(2, years.length)}
      spacingX={0}
      spacingY={8}
      pb={4}
    >
      <BreakdownView1065
        currentYear={currentYear}
        selectedResult={selectedResult}
        showFullBreakdown={true}
      />
    </SimpleGrid>
  );
};

const TwoYearBreakdown = ({
  years,
  currentYear,
  isPositiveTrend,
  formula,
  selectedResult,
}: Readonly<TwoYearBreakdownProps>) => {
  const priorYear = years[1];

  return (
    <>
      {(isPositiveTrend !== 'false' && (
        <SimpleGrid
          w={`${50 * Math.min(years.length, 2)}%`}
          columns={Math.min(2, years.length)}
          spacingX={0}
          spacingY={8}
          pb={4}
        >
          <Box pr={4}>
            <BreakdownView1065
              currentYear={currentYear}
              selectedResult={selectedResult}
              showFullBreakdown={true}
            />
          </Box>
          <BreakdownView1065
            currentYear={priorYear}
            selectedResult={selectedResult}
            showFullBreakdown={true}
          />
        </SimpleGrid>
      )) || (
        <>
          {(formula?.formulaId === FORMULA.OneYrNegBreakdownFormulaId ||
            formula?.formulaId === FORMULA.TwoYrNegBreakdownFormulaId) && (
            <SimpleGrid
              w={`${50 * Math.min(years.length, 2)}%`}
              columns={Math.min(2, years.length)}
              spacingX={0}
              spacingY={8}
              pb={4}
            >
              <Box pr={4}>
                <BreakdownView1065
                  currentYear={currentYear}
                  selectedResult={selectedResult}
                  showFullBreakdown={false}
                />
              </Box>
              <BreakdownView1065
                currentYear={priorYear}
                selectedResult={selectedResult}
                showFullBreakdown={false}
              />
            </SimpleGrid>
          )}
          {formula?.formulaId === FORMULA.BusinessIncomeFormulaId && (
            <SimpleGrid
              w={`${50 * Math.min(years.length, 2)}%`}
              columns={Math.min(2, years.length)}
              spacingX={0}
              spacingY={8}
              pb={4}
            >
              <Box pr={4}>
                <BreakdownView1065
                  currentYear={currentYear}
                  selectedResult={selectedResult}
                  showFullBreakdown={true}
                />
              </Box>
              <BreakdownView1065
                currentYear={priorYear}
                selectedResult={selectedResult}
                showFullBreakdown={true}
              />
            </SimpleGrid>
          )}
        </>
      )}
    </>
  );
};

const Form1065CalculationBreakdown = ({
  selectedResult,
  onNewWindow,
  hideAccordion = false,
}: Readonly<Form1065CalculationBreakdownProps>) => {
  const years = selectedResult
    .getFiscalYears()
    .filter((year) => year !== 0)
    .sort((a, b) => b - a);

  const currentYear = years[0];

  const isPositiveTrend = selectedResult
    .getInput(FormTypes.Form1065Output, 0, 'isPositiveTrend')
    ?.fieldValue?.toLowerCase();

  const formula = selectedResult.formulas[0];

  return (
    <BreakdownContainer
      header="1065 Calculation Breakdown"
      onNewWindow={onNewWindow}
      hideAccordion={hideAccordion}
    >
      <Box>
        {formula && (
          <Text pb={4} fontSize="sm">
            <Text as="span" fontWeight="bold">
              Monthly Income Equation:&nbsp;
            </Text>
            {formula.formula}
          </Text>
        )}
        {years.length === 1 && (
          <OneYearBreakdown
            years={years}
            currentYear={currentYear}
            selectedResult={selectedResult}
          />
        )}

        {years.length >= 2 && (
          <>
            <TwoYearBreakdown
              years={years}
              currentYear={currentYear}
              isPositiveTrend={isPositiveTrend}
              formula={formula}
              selectedResult={selectedResult}
            />
            <TrendingAnalysis selectedResult={selectedResult} />
          </>
        )}
      </Box>
    </BreakdownContainer>
  );
};

export default Form1065CalculationBreakdown;
